import { difference, uniq } from 'lodash-es';
import { FC, JSX } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { Content } from '@/entities/content/api/types';
import { useGetAdUnitsSearchQuery } from '@/entities/sites/api/sitesApi';
import PageFilter from '@/features/page-filter';
import Input from '@/shared/ui/Input';

import { PublicationSettings } from './PublicationSettings';

import s from './MetadataSettings.module.css';

export interface IBaseSettingsProps {}

export const BaseSettings: FC<IBaseSettingsProps> = (): JSX.Element => {
  const {
    control,
    register,
    setValue,
    getValues,
    clearErrors,
    formState: { errors },
  } = useFormContext<Content>();
  const sitesSelected = useWatch({
    control,
    name: 'sites',
  });

  const getAdUnitsSearchQuery = useGetAdUnitsSearchQuery();

  // should go before onChange because it uses watch('sites')
  const handleSitesChange = async (value: number[]) => {
    if (!getAdUnitsSearchQuery.data) return;

    const selectedSites = difference(value, sitesSelected);
    const deselectedSites = difference(sitesSelected, value);

    const currentAdUnits: number[] = getValues('zones') || [];

    if (selectedSites.length > 0) {
      const adUnitsToAdd = getAdUnitsSearchQuery.data
        .filter((adUnit) => selectedSites.includes(adUnit.site_id))
        .map((adUnit) => adUnit.id);

      setValue('zones', uniq([...currentAdUnits, ...adUnitsToAdd]));
      clearErrors('zones');
    }

    if (deselectedSites.length > 0) {
      const adUnitsToRemove = getAdUnitsSearchQuery.data
        .filter((adUnit) => deselectedSites.includes(adUnit.site_id))
        .map((adUnit) => adUnit.id);

      setValue('zones', difference(currentAdUnits, adUnitsToRemove));
      clearErrors('zones');
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-[12px] bg-[#f8f8f9]/[0.56] p-4 dark:bg-white/[.02]">
      <div className="text-base font-semibold text-black dark:text-white">
        Base
      </div>
      <div className="flex flex-col gap-6">
        <Input
          className={s.finput}
          placeholder="Name"
          type="text"
          {...register('name')}
          error={errors?.name ? errors?.name.message : ''}
          errorClassName={
            errors?.name?.message?.length ? 'text-sm font-light' : 'hidden'
          }
        />

        <div className={`flex w-full flex-col gap-2 ${s.ffield}`}>
          <Controller
            control={control}
            name="sites"
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <>
                <PageFilter.SiteSelect
                  value={value}
                  onChange={(ss) => {
                    onChange(ss);
                    handleSitesChange(ss);
                  }}
                  className="!min-w-[100px]"
                />
                {error && (
                  <div className="text-sm font-light text-red">
                    {error?.message}
                  </div>
                )}
              </>
            )}
          />
        </div>

        <div className={`flex w-full flex-col gap-2 ${s.ffield}`}>
          <Controller
            control={control}
            name="categories"
            render={({
              field: { value = [], onChange },
              fieldState: { error },
            }) => (
              <>
                <PageFilter.ContentCategorySelect
                  value={value}
                  onChange={onChange}
                  disabled={!sitesSelected.length}
                />
                {error && (
                  <div className="text-sm font-light text-red">
                    {error?.message}
                  </div>
                )}
              </>
            )}
          />
        </div>

        <PublicationSettings />

        <div className={`flex w-full flex-col gap-2 ${s.ffield}`}>
          <Controller
            control={control}
            name="zones"
            render={({
              field: { value = [], onChange },
              fieldState: { error },
            }) => (
              <>
                <PageFilter.AdUnitsSelect
                  sitesSelected={sitesSelected}
                  value={value}
                  onChange={onChange}
                  disabled={!sitesSelected.length}
                />
                {error && (
                  <div className="text-sm font-light text-red">
                    {error?.message}
                  </div>
                )}
              </>
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default BaseSettings;
